import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { Camera, Upload, User, Phone, Mail } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const PetIdentificationPage = () => {
  const { themeClasses } = useTheme();
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [identificationResult, setIdentificationResult] = useState(null);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsIdentifying(true);
    setIdentificationResult(null);

    try {
      // Convert image to base64 with compression
      const imageBase64 = await convertToBase64(file);

      // Call single image identification function
      const identifyPetFn = httpsCallable(functions, "identifyPet");
      const response = await identifyPetFn({
        imageBase64: imageBase64,
        location: "Web Upload",
      });

      setIdentificationResult(response.data);
    } catch (error) {
      console.error("Identification failed:", error);
      setIdentificationResult({
        success: false,
        error: "Failed to identify pet: " + error.message,
        confidence: 0,
      });
    } finally {
      setIsIdentifying(false);
    }
  };

  // Helper function to convert file to base64 with compression
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions (max 1024px)
        const maxSize = 1024;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL("image/jpeg", 0.8);

        console.log(
          "Converting to base64:",
          file.name,
          file.type,
          "Size:",
          file.size
        );
        console.log(
          "Image compressed from",
          file.size,
          "to ~",
          Math.round(base64.length * 0.75),
          "bytes"
        );

        resolve(base64);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  return (
    <div className={`min-h-screen ${themeClasses.bg.primary}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1
            className={`text-4xl font-bold ${themeClasses.text.primary} mb-4`}
          >
            🔍 Pet Identification
          </h1>
          <p
            className={`text-lg ${themeClasses.text.secondary} max-w-2xl mx-auto`}
          >
            Upload a photo of a pet to identify them using our advanced AI
            system. Our biometric matching can help reunite lost pets with their
            families.
          </p>
        </div>

        {/* Upload Section */}
        <div
          className={`max-w-2xl mx-auto ${themeClasses.bg.secondary} rounded-lg p-8 border ${themeClasses.border.primary} mb-8`}
        >
          <div className="text-center">
            <div
              className={`w-16 h-16 ${themeClasses.bg.button} rounded-full flex items-center justify-center mx-auto mb-4`}
            >
              <Camera className="w-8 h-8 text-white" />
            </div>
            <h3
              className={`text-xl font-semibold ${themeClasses.text.primary} mb-2`}
            >
              Upload Pet Photo
            </h3>
            <p className={`${themeClasses.text.secondary} mb-6`}>
              Choose a clear photo showing the pet's face and body for best
              results
            </p>

            <label
              className={`inline-flex items-center ${themeClasses.bg.button} text-white px-6 py-3 rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
            >
              <Upload className="w-5 h-5 mr-2" />
              Choose Photo
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={isIdentifying}
              />
            </label>
          </div>
        </div>

        {/* Loading State */}
        {isIdentifying && (
          <div
            className={`max-w-2xl mx-auto ${themeClasses.bg.secondary} rounded-lg p-8 border ${themeClasses.border.primary} text-center`}
          >
            <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className={`${themeClasses.text.primary} font-medium`}>
              Analyzing image with AI...
            </p>
          </div>
        )}

        {/* Results Section */}
        {identificationResult && (
          <div
            className={`mt-6 ${themeClasses.bg.secondary} rounded-lg p-6 border ${themeClasses.border.primary}`}
          >
            {!identificationResult.success || identificationResult.error ? (
              <div className="text-center text-red-400">
                <p>
                  Error: {identificationResult.error || "Identification failed"}
                </p>
              </div>
            ) : identificationResult.matches &&
              identificationResult.matches.length > 0 ? (
              <div>
                <div className="text-center mb-6">
                  <h4
                    className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
                  >
                    🎉 Pet Successfully Identified!
                  </h4>
                  <p className="text-green-400 text-sm">
                    Found {identificationResult.matches.length} potential
                    match(es)
                  </p>
                </div>

                {/* Display all matches */}
                {identificationResult.matches.map((match, index) => (
                  <div key={match.petId || index} className="mb-6 last:mb-0">
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Pet Photo */}
                      {match.avatarThumbnail && (
                        <div className="flex justify-center">
                          <div className="relative">
                            <div className="w-40 h-40 rounded-xl overflow-hidden border-3 border-gradient-to-r from-cyan-400 to-purple-500 shadow-lg shadow-cyan-500/25">
                              <img
                                src={match.avatarThumbnail}
                                alt={match.name || "Pet"}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                              ✅ VERIFIED
                            </div>
                            <div className="absolute -bottom-2 -left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                              {Math.round(match.similarity * 100)}% Match
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pet Information */}
                      <div
                        className={
                          match.avatarThumbnail
                            ? "md:col-span-1"
                            : "md:col-span-2"
                        }
                      >
                        <div className="space-y-3">
                          {/* Pet Name - Hero Style */}
                          {match.name && (
                            <div className="text-center md:text-left">
                              <h3
                                className={`text-3xl font-bold ${themeClasses.text.accent}`}
                              >
                                {match.name}
                              </h3>
                            </div>
                          )}

                          {/* Pet ID Badge */}
                          <div
                            className={`inline-flex items-center ${themeClasses.bg.secondary} border ${themeClasses.border.accent} rounded-lg px-3 py-2`}
                          >
                            <span
                              className={`${themeClasses.text.accent} text-sm font-mono`}
                            >
                              ID: {match.petId || "N/A"}
                            </span>
                          </div>

                          {/* Species & Breed */}
                          <div
                            className={`${themeClasses.bg.card} rounded-lg p-4 border ${themeClasses.border.primary}`}
                          >
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p
                                  className={`${themeClasses.text.secondary} text-sm`}
                                >
                                  Species
                                </p>
                                <p
                                  className={`${themeClasses.text.primary} font-semibold`}
                                >
                                  {match.species || "Unknown"}
                                </p>
                              </div>
                              <div>
                                <p
                                  className={`${themeClasses.text.secondary} text-sm`}
                                >
                                  Breed
                                </p>
                                <p
                                  className={`${themeClasses.text.primary} font-semibold`}
                                >
                                  {match.breed || "Mixed Breed"}
                                </p>
                              </div>
                            </div>

                            {match.age && (
                              <div
                                className={`mt-3 pt-3 border-t ${themeClasses.border.primary}`}
                              >
                                <p
                                  className={`${themeClasses.text.secondary} text-sm`}
                                >
                                  Age
                                </p>
                                <p
                                  className={`${themeClasses.text.primary} font-semibold`}
                                >
                                  {match.age} years old
                                </p>
                              </div>
                            )}
                          </div>

                          {/* AI Confidence */}
                          <div
                            className={`${themeClasses.bg.secondary} border ${themeClasses.border.primary} rounded-lg p-4`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className={`${themeClasses.text.primary} font-medium`}
                              >
                                Match Confidence
                              </span>
                              <span
                                className={`${themeClasses.text.accent} font-bold text-lg`}
                              >
                                {Math.round(match.similarity * 100)}%
                              </span>
                            </div>
                            <div
                              className={`${themeClasses.bg.input} rounded-full h-2`}
                            >
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-400 h-2 rounded-full transition-all duration-1000"
                                style={{ width: `${match.similarity * 100}%` }}
                              />
                            </div>
                            <p
                              className={`${themeClasses.text.accent} text-xs mt-2 flex items-center`}
                            >
                              <span className="mr-1">✨</span>
                              Biometric Match: {match.confidence || "High"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Owner/Contact Information */}
                      <div className="space-y-4">
                        <div className="text-center">
                          <h5
                            className={`text-xl font-bold ${themeClasses.text.primary} mb-2 flex items-center justify-center`}
                          >
                            <User
                              className={`w-5 h-5 mr-2 ${themeClasses.text.accent}`}
                            />
                            Owner Information
                          </h5>
                        </div>

                        {match.owner ? (
                          <div className="space-y-4">
                            {/* Owner Details */}
                            <div
                              className={`${themeClasses.bg.card} rounded-xl p-4 border ${themeClasses.border.primary} space-y-3`}
                            >
                              <div className="text-center">
                                <div
                                  className={`w-12 h-12 ${themeClasses.bg.button} rounded-full flex items-center justify-center mx-auto mb-2`}
                                >
                                  <User className="w-6 h-6 text-white" />
                                </div>
                                <h6
                                  className={`${themeClasses.text.primary} font-bold text-lg`}
                                >
                                  {match.owner.name ||
                                    match.owner.displayName ||
                                    "Pet Owner"}
                                </h6>
                              </div>

                              <div className="space-y-2">
                                {match.owner.email && (
                                  <div
                                    className={`flex items-center justify-center space-x-2 ${themeClasses.bg.secondary} rounded-lg p-2`}
                                  >
                                    <Mail
                                      className={`w-4 h-4 ${themeClasses.text.accent}`}
                                    />
                                    <a
                                      href={`mailto:${match.owner.email}`}
                                      className={`${themeClasses.text.accent} hover:opacity-80 underline text-sm`}
                                    >
                                      {match.owner.email}
                                    </a>
                                  </div>
                                )}

                                {match.owner.phone &&
                                  match.owner.phone !== "Not provided" && (
                                    <div
                                      className={`flex items-center justify-center space-x-2 ${themeClasses.bg.secondary} rounded-lg p-2`}
                                    >
                                      <Phone
                                        className={`w-4 h-4 ${themeClasses.text.accent}`}
                                      />
                                      <a
                                        href={`tel:${match.owner.phone}`}
                                        className={`${themeClasses.text.accent} hover:opacity-80 underline text-sm`}
                                      >
                                        {match.owner.phone}
                                      </a>
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className={`${themeClasses.bg.secondary} border ${themeClasses.border.primary} rounded-xl p-4 text-center`}
                          >
                            <p className={themeClasses.text.secondary}>
                              Owner contact information not available
                            </p>
                          </div>
                        )}

                        {/* Metadata */}
                        <div
                          className={`${themeClasses.bg.secondary} rounded-xl p-4 border ${themeClasses.border.primary}`}
                        >
                          <h6
                            className={`${themeClasses.text.primary} font-medium mb-3 text-center`}
                          >
                            📍 Match Details
                          </h6>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className={themeClasses.text.secondary}>
                                Status:
                              </span>
                              <span className={themeClasses.text.primary}>
                                {match.status || "Active"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className={themeClasses.text.secondary}>
                                Last Seen:
                              </span>
                              <span className={themeClasses.text.primary}>
                                {match.lastSeen
                                  ? new Date(
                                      match.lastSeen
                                    ).toLocaleDateString()
                                  : "Recently"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <div
                  className={`${themeClasses.bg.secondary} rounded-lg p-8 border ${themeClasses.border.primary}`}
                >
                  <h4
                    className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}
                  >
                    No Matches Found
                  </h4>
                  <p className={themeClasses.text.secondary}>
                    {identificationResult.message ||
                      "No registered pets match this image"}
                  </p>
                  <div className="mt-4">
                    <p className={`${themeClasses.text.secondary} text-sm`}>
                      Processing Time:{" "}
                      {identificationResult.processingTimeMs || 0}ms
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PetIdentificationPage;
